// "View chunks" modal — shown when an operator clicks a document row in the
// KG-page Info sidebar's document list. Renders what LightRAG actually stored
// for that doc after chunking: one row per chunk, ordered by position, showing
// the index, token count, the originating `## heading` if any, and the chunk
// content (collapsed by default for long chunks).
//
// Purpose is diagnosis: confirm that the heading-aware chunker did the right
// thing for this doc (one chunk per `##` section) and surface cases where
// adjacent sections fused into the same chunk — the failure mode the chunker
// exists to prevent.
import { useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';

import { useMetaphysicsKg, type IDocChunk, type IKgOwner } from '../../lib/metaphysics-kg';
import { Modal } from '../common/Modal';

export interface IChunksModalProps {
  open: boolean;
  onClose: () => void;
  owner: IKgOwner | null;
  kgVersion: number | null;
  docId: string | null;
  docName: string | null;
}

export function ChunksModal({
  open,
  onClose,
  owner,
  kgVersion,
  docId,
  docName,
}: IChunksModalProps) {
  const kg = useMetaphysicsKg();

  const chunks = useQuery({
    queryKey: ['mp', 'kg', 'doc-chunks', kgVersion, docId],
    queryFn: () => {
      if (!owner || !docId) return Promise.resolve([] as IDocChunk[]);
      return kg.getDocChunks(owner, kgVersion, docId);
    },
    enabled: open && !!owner && !!docId,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="KNOWLEDGE GRAPH"
      title={docName ? `Chunks · ${docName}` : 'Chunks'}
      width="min(900px, 96vw)"
    >
      <div style={bodyStyle}>
        {chunks.isLoading ? (
          <div className="t-sm t-muted">Loading chunks…</div>
        ) : chunks.error ? (
          <div className="alert alert--danger">{(chunks.error as Error).message}</div>
        ) : (chunks.data?.length ?? 0) === 0 ? (
          <div className="t-sm t-muted">
            No chunks found for this document. It may have failed to ingest, been deleted, or be in
            a different workspace.
          </div>
        ) : (
          <>
            <div className="t-xs t-muted" style={{ marginBottom: 'var(--sp-2)' }}>
              {chunks.data?.length} chunks · sorted by in-document position.
            </div>
            {chunks.data?.map((c) => (
              <ChunkRow key={c.id} chunk={c} />
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}

const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  maxHeight: '70vh',
  overflowY: 'auto',
};

function ChunkRow({ chunk }: { chunk: IDocChunk }) {
  const [expanded, setExpanded] = useState(false);
  const heading = extractHeading(chunk.content);
  const previewCap = 320;
  const isLong = chunk.content.length > previewCap;
  const shown = expanded || !isLong ? chunk.content : chunk.content.slice(0, previewCap) + '…';
  return (
    <div style={rowStyle}>
      <div style={headerStyle}>
        <span className="t-mono-label">#{chunk.chunkOrderIndex}</span>
        <span className="t-xs t-muted">{chunk.tokens} tokens</span>
        {heading ? (
          <span className="t-xs" style={{ color: 'var(--text-secondary)' }}>
            {heading}
          </span>
        ) : null}
      </div>
      <pre style={preStyle}>{shown}</pre>
      {isLong ? (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Collapse' : 'Show full chunk'}
        </button>
      ) : null}
    </div>
  );
}

const rowStyle: CSSProperties = {
  border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
  borderRadius: 'var(--radius-sm, 4px)',
  padding: 'var(--sp-2)',
  background: 'var(--surface-1, rgba(255,255,255,0.02))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-1)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--sp-2)',
  alignItems: 'baseline',
};

const preStyle: CSSProperties = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 11,
  lineHeight: 1.45,
  color: 'var(--text-primary)',
  background: 'transparent',
};

/**
 * Find the first `## …` heading line in the chunk content. The heading-aware
 * chunker emits one section per chunk so this is normally the chunk's
 * section. Falls back to first non-empty line if no `##` is present so the
 * row still shows useful context for fallthrough (token-chunked) chunks.
 * Returns null when the content is empty.
 */
function extractHeading(content: string): string | null {
  const lines = content.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('## ')) return line;
    // First-line fallback — useful when the chunker fell through.
    return line.length > 90 ? line.slice(0, 90) + '…' : line;
  }
  return null;
}
